import {
  ColDef,
  GridApi,
  GridOptions,
  GridReadyEvent,
  Module,
} from '@ag-grid-community/core'
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core'
import {
  enableHeliosGridDensityLevel,
  heliosColumnTypes,
} from '@helios/ag-grid-config'
import { HeliosMessage } from '@helios/messaging'
import { DensityLevel, UserPreferencesService } from '@helios/user-preferences'
import { NfColumnTypes } from '@northfork/ag-grid'
import { composeFieldPath } from '@northfork/basics'
import { ToastService } from '@northfork/ng-basics'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

export interface Devtools extends HeliosMessage<unknown> {
  sseType: 'Session' | 'Global'
  namespace: string
}

const field = composeFieldPath([] as Devtools[])

@Component({
  selector: 'hs-devtools-grid',
  templateUrl: './devtools-grid.component.html',
  styleUrls: ['./devtools-grid.component.scss'],
})
export class DevtoolsGridComponent implements OnDestroy {
  gridOptions: GridOptions = this.createGridOptions()
  _data: Devtools[] = []
  @Input()
  set eventStream(value: Devtools | null) {
    if (value) {
      this.gridApi?.applyTransaction({
        add: [value],
      })
    }
  }
  gridModules: Module[] = []
  isPlaying = false
  @Output() toggleIsPlaying = new EventEmitter<boolean>()
  @Output() clear = new EventEmitter<void>()
  @Input() token: string | null | undefined = null
  gridApi!: GridApi
  private readonly _destroyed$ = new Subject<void>()

  constructor(
    private readonly preferences: UserPreferencesService,
    private readonly toastService: ToastService,
  ) {}

  toggleStream() {
    this.isPlaying = !this.isPlaying
    this.toggleIsPlaying.emit(this.isPlaying)
  }

  ngOnDestroy(): void {
    this._destroyed$.next()
    this._destroyed$.complete()
    this.gridApi?.destroy()
  }

  copyAuthToken() {
    navigator.clipboard.writeText(this.token ?? '')
    this.toastService.success('Copied auth token to clipboard')
  }

  clearData() {
    this.gridApi.setRowData([])
    this.clear.emit()
  }

  private createGridOptions(): GridOptions {
    return {
      columnTypes: heliosColumnTypes,
      columnDefs: this.createBaseColumnDefs(),
      onGridReady: event => this.onGridReady(event),
      defaultColDef: {
        sortable: true,
        filter: true,
      },
      suppressCopyRowsToClipboard: true,
    }
  }

  /**
   * @internal
   * @param params
   */
  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api

    this.preferences
      .getDensityLevel()
      .pipe(takeUntil(this._destroyed$))
      .subscribe(level =>
        enableHeliosGridDensityLevel(
          params.api,
          DensityLevel.Compact === level,
        ),
      )
  }

  private createBaseColumnDefs(): ColDef[] {
    return [
      {
        field: field('sseType'),
        headerName: 'SSE Type',
        width: 68,
      },
      {
        field: field('heliosSessionId'),
      },
      {
        field: field('timestampUtc'),
        type: NfColumnTypes.DateTimePrecise,
        valueGetter: params => new Date(params.data.timestampUtc),
      },
      {
        field: field('eventType'),
      },
      {
        field: field('namespace'),
        width: 82,
      },
      {
        field: field('payload'),
        valueGetter: params => JSON.stringify(params.data.payload ?? {}),
        width: 300,
      },
    ]
  }
}
